import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Col, Container, Row } from 'reactstrap';
import { addMessage } from 'store/messages/actions';

import apiProjects from 'api/projects';
import IProject from 'components/interfaces/IProject';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import SaveProjectModal from 'components/layout/navbar/SaveProjectModal';
import BasicMaterialTable from 'components/layout/tables/BasicMaterialTable';
import translations from '../../services/translations/translations';
import { convertToReadableDate } from '../configurator/services/formatters/convertToReadableDatets';

import 'style/configurator/Configurator.css';
import 'style/layout/Layout.css';

import { fetchUsers } from '../../store/users/actions';
import IUserGet from '../../api/interfaces/users/IUserGet';
import { convertToReadableDecimal } from '../configurator/services/formatters/convertToReadableDecimal';
import CreateProjectModal from '../layout/navbar/CreateProjectModal';

const Projects = ({
  fetchUsersList,
  usersList,
}: {
  fetchUsersList: () => void;
  usersList: IUserGet[];
}): JSX.Element => {
  const { t } = useTranslation([
    translations.common,
    translations.glassWallProperties,
  ]);
  const dispatch = useDispatch();
  const history = useHistory();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>();
  const [editingProject, setEditingProject] = useState<IProject>();
  const currentUser = useSelector((state: any) => state.users.current);
  const PROJECTS_FILTER_SESSION =
    'projects-filter-glass-konfigurator-local-storage';

  const pageSize = 250;

  const projectsTable = useRef();

  useEffect(() => {
    if (selectedRow !== undefined) {
      history.push(`/projects/${selectedRow?.id}`);
    }
  }, [history, selectedRow]);

  useEffect(() => {
    fetchUsersList();
  }, []);

  const getColumnFieldFromFilter = (query: any, fieldName: string): any => {
    return query.filters?.find((f: any) => f.column.field === fieldName)?.value;
  };

  const translateWallTypes = (wallTypes?: string): string => {
    return wallTypes !== undefined && wallTypes !== null
      ? wallTypes
          .split(',')
          .map((type) => {
            return t(
              `${translations.glassWallProperties}:glazingWallTypes:${type}`
            );
          })
          .join(',')
      : '';
  };

  const getProjects = async (query: any) => {
    try {
      const search = getColumnFieldFromFilter(query, 'name');
      const status = getColumnFieldFromFilter(query, 'status');
      const created = getColumnFieldFromFilter(query, 'created');
      const updated = getColumnFieldFromFilter(query, 'updated');
      const createdUserId = getColumnFieldFromFilter(query, 'createdUser');
      const updatedUserId = getColumnFieldFromFilter(query, 'updatedUser');
      let squareMeters = getColumnFieldFromFilter(query, 'squareMeters');
      const wallTypes = getColumnFieldFromFilter(query, 'wallTypes');

      squareMeters = squareMeters !== undefined ? +squareMeters : undefined;

      const result: IProject[] = await apiProjects.getAll({
        limit: query.pageSize,
        offset: query.page === 0 ? 0 : query.page * query.pageSize,
        search,
        status: status?.join(','),
        created: created || null,
        createdUserId: createdUserId?.join(','),
        updated: updated || null,
        updatedUserId: updatedUserId?.join(','),
        squareMeters,
        wallTypes,
      });
      const count = await apiProjects.count({
        search,
        status: status?.join(','),
        created: created || null,
        createdUserId: createdUserId?.join(','),
        updated: updated || null,
        updatedUserId: updatedUserId?.join(','),
        squareMeters,
        wallTypes,
      });

      const resultWithReadableFields = result.map((proj) => ({
        ...proj,
        created: convertToReadableDate(proj.created),
        createdUser: proj.createdUser?.id,
        updated: convertToReadableDate(proj.updated),
        updatedUser: proj.updatedUser?.id,
        squareMeters: convertToReadableDecimal(proj.squareMeters),
        wallTypes: translateWallTypes(proj.wallTypes),
      }));

      return {
        data: resultWithReadableFields,
        page: query.page,
        totalCount: count.count,
      };
    } catch (error) {
      return {};
    }
  };

  const addProject = () => {
    setShowCreateModal(true);
  };

  const save = async (project: IProject) => {
    if (project.name === '') {
      dispatch(
        addMessage(t(`${translations.common}:Modal Messages.Fill All Fields`))
      );
      return;
    }
    try {
      const result = await apiProjects.put(project.id, project);
      setEditingProject(undefined);
      if (projectsTable?.current != null) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        projectsTable.current.reload();
      }
    } catch (error) {
      dispatch(
        addMessage(t(`${translations}:Modal Messages.Project creation failed`))
      );
    }
  };

  const create = (name: string, status: string) => {
    if (name === '') {
      dispatch(
        addMessage(t(`${translations.common}:Modal Messages.Fill All Fields`))
      );
      return;
    }
    const submit = async () => {
      try {
        const result = await apiProjects.post({ name, status });
        history.push(`/projects/${result.id}`);
      } catch (error) {
        dispatch(
          addMessage(
            t(`${translations}:Modal Messages.Project creation failed`)
          )
        );
      }
    };
    submit().catch((e) => console.log(e));
  };

  const colTitles = {
    name: t(`${translations.common}:Name`),
    status: t(`${translations.common}:Status`),
    created: t(`${translations.common}:Created`),
    createdUser: t(`${translations.common}:CreatedUser`),
    updated: t(`${translations.common}:Updated`),
    updatedUser: t(`${translations.common}:UpdatedUser`),
    squareMeters: t(`${translations.common}:SquareMeters`),
    wallTypes: t(`${translations.common}:WallTypes`),
  };

  usersList.sort((u1, u2) => {
    const n1 = (u1.firstName + u1.lastName).toLowerCase();
    const n2 = (u2.firstName + u2.lastName).toLowerCase();
    return n1.localeCompare(n2);
  });

  const usersFilter: any = {};

  usersList.forEach((u) => {
    const key = u.id ? u.id : '';
    if (u.id == null) {
      return false;
    }
    usersFilter[key] = `${u.firstName} ${u.lastName}`;
    return true;
  });

  const getDefaultFilter = (field: string) => {
    const storedFilters = window.localStorage.getItem(PROJECTS_FILTER_SESSION);
    if (storedFilters == null) {
      return null;
    }

    const lastUsedFilters = JSON.parse(storedFilters);
    if (lastUsedFilters == null || lastUsedFilters.length <= 0) {
      return null;
    }

    const result = lastUsedFilters.find(
      (lastUsedfilter: any) => lastUsedfilter.column.field === field
    );

    return result != null ? result.value : null;
  };

  const statusFormatter = (cell: any, row: any) =>
    cell == null ? '' : t(`${translations.common}:statusTypes:${cell}`);

  const columns = [
    {
      field: 'name',
      title: colTitles.name,
      defaultFilter: getDefaultFilter('name'),
    },
    {
      field: 'status',
      title: colTitles.status,
      defaultFilter: getDefaultFilter('status'),
      lookup: t(`${translations.common}:statusTypes`, { returnObjects: true }),
      render: (cell: any) => statusFormatter(cell.status, 'status'),
    },
    {
      field: 'created',
      title: colTitles.created,
      type: 'date',
      defaultFilter: getDefaultFilter('created'),
      dateSetting: { locale: 'fi-FI' },
    },
    {
      field: 'createdUser',
      title: colTitles.createdUser,
      defaultFilter: getDefaultFilter('createdUser'),
      lookup: usersFilter,
    },
    {
      field: 'updated',
      title: colTitles.updated,
      type: 'date',
      defaultFilter: getDefaultFilter('updated'),
      dateSetting: { locale: 'fi-FI' },
    },
    {
      field: 'updatedUser',
      title: colTitles.updatedUser,
      defaultFilter: getDefaultFilter('updatedUser'),
      lookup: usersFilter,
    },
    {
      field: 'squareMeters',
      title: colTitles.squareMeters,
      defaultFilter: getDefaultFilter('squareMeters'),
    },
    {
      field: 'wallTypes',
      title: colTitles.wallTypes,
      defaultFilter: getDefaultFilter('wallTypes'),
    },
  ];

  const actions = [
    {
      icon: 'edit',
      tooltip: t(`${translations.common}:Edit`),
      onClick: async (event: any, rowData: any) => {
        const p: IProject = await apiProjects.get(rowData.id);
        setEditingProject(p);
      },
    },
  ];

  const allowedToCreateProject =
    currentUser !== null &&
    (currentUser.allowedToWriteConfigurations ||
      currentUser.allowedToWriteOrganizationUnitProjects);

  return (
    <Container fluid className="main-container">
      <Row className="row-content">
        <Col>
          <h2>{t(`${translations.common}:Projects`)}</h2>
        </Col>
      </Row>
      <div className="grey-background">
        <Row>
          {allowedToCreateProject ? (
            <Col>
              <PrimaryButton className="move-right" onClick={addProject}>
                {t(`${translations.common}:Add Project`).toLocaleUpperCase()}
              </PrimaryButton>
            </Col>
          ) : null}
        </Row>
        <Row>
          <Col>
            <BasicMaterialTable
              ref={projectsTable}
              columns={columns}
              fetchData={getProjects}
              sizePerPage={pageSize}
              setSelectedRow={setSelectedRow}
              localStorageTag={PROJECTS_FILTER_SESSION}
              actions={actions}
            />
          </Col>
        </Row>
      </div>
      <SaveProjectModal
        editingProject={editingProject}
        setEditingProject={setEditingProject}
        save={save}
      />
      <CreateProjectModal
        show={showCreateModal}
        setShow={setShowCreateModal}
        create={create}
        placeholder=""
      />
    </Container>
  );
};

const mapStateToProps = (state: any) => ({
  usersList: state.users.users,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchUsersList: () => dispatch(fetchUsers()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Projects);
